/* eslint-disable no-undef */
import { useState, useEffect } from 'react'

import { mySalesService } from '../Services/mySalesService'

const DEFAULT_REGULAR_DISCOUNT = '50'
const DEFAULT_SUPER_DISCOUNT = '70'

export const useDiscounts = ({ forceOptInEnabled = false } = {}) => {
  const [regularDiscount, setRegularDiscount] = useState(
    DEFAULT_REGULAR_DISCOUNT
  )
  const [superDiscount, setSuperDiscount] = useState(DEFAULT_SUPER_DISCOUNT)
  const [participateAll, setParticipateAll] = useState(false)
  const [selectAllPrograms, setSelectAllPrograms] = useState(false)
  const [sellForEquity, setSellForEquity] = useState(false)
  const [sellOnOtherPlatforms, setSellOnOtherPlatforms] = useState(false)

  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  const [originalData, setOriginalData] = useState(null)
  const [hasChanges, setHasChanges] = useState(false)

  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false)
  const [pendingLeaveAction, setPendingLeaveAction] = useState(null)
  const [showForceOptInWarning, setShowForceOptInWarning] = useState(false)

  useEffect(() => {
    const fetchDiscounts = async () => {
      try {
        setIsLoading(true)
        const data = await mySalesService.getDiscounts()

        const serverSellForEquity = data?.sellForEquity ?? false
        const serverSellOnOtherPlatforms = data?.sellOnOtherPlatforms ?? false
        // Backend-provided flag set when a user explicitly opts out during the
        // force-opt-in window. Prevents the warning from resurrecting on reload.
        const hasOptedOut = data?.maximizeEarningsOptedOut ?? false
        const shouldPreCheck = (
          forceOptInEnabled
          && !serverSellForEquity
          && !serverSellOnOtherPlatforms
          && !hasOptedOut
        )

        setRegularDiscount(
          data?.discount === null
            ? DEFAULT_REGULAR_DISCOUNT
            : data.discount.toString()
        )
        setSuperDiscount(
          data?.superDiscount === null
            ? DEFAULT_SUPER_DISCOUNT
            : data.superDiscount.toString()
        )
        setParticipateAll(data?.saleOff ?? false)
        setSellForEquity(shouldPreCheck ? true : serverSellForEquity)
        setSellOnOtherPlatforms(shouldPreCheck ? true : serverSellOnOtherPlatforms)
        setSelectAllPrograms(
          shouldPreCheck
            ? true
            : (serverSellForEquity && serverSellOnOtherPlatforms)
        )
        setShowForceOptInWarning(shouldPreCheck)
        setOriginalData(data)
      } catch (error) {
        App.events.publish(
          'app:notification:flash:error',
          'Unable to load discount settings. Please try again.'
        )
      } finally {
        setIsLoading(false)
      }
    }

    fetchDiscounts()
  }, [forceOptInEnabled])

  useEffect(() => {
    if (originalData) {
      const currentValues = {
        discount: regularDiscount,
        superDiscount,
        saleOff: participateAll,
        selectAllPrograms,
        sellForEquity,
        sellOnOtherPlatforms,
      }

      const serverSellForEquity = originalData.sellForEquity ?? false
      const serverSellOnOtherPlatforms = originalData.sellOnOtherPlatforms ?? false

      const originalValues = {
        discount: originalData.discount?.toString() || DEFAULT_REGULAR_DISCOUNT,
        superDiscount:
          originalData.superDiscount?.toString() || DEFAULT_SUPER_DISCOUNT,
        saleOff: originalData.saleOff ?? false,
        sellForEquity: serverSellForEquity,
        sellOnOtherPlatforms: serverSellOnOtherPlatforms,
        // Derived to match the fetch/handler logic; reading the raw field
        // would false-positive when the server payload omits it.
        selectAllPrograms: serverSellForEquity && serverSellOnOtherPlatforms,
      }

      const hasUnsavedChanges = Object.keys(currentValues).some(
        (key) => currentValues[key] !== originalValues[key]
      )

      setHasChanges(hasUnsavedChanges)
    }
  }, [
    regularDiscount,
    superDiscount,
    participateAll,
    selectAllPrograms,
    sellForEquity,
    sellOnOtherPlatforms,
    originalData,
  ])

  const handleRegularDiscountChange = (value) => {
    setRegularDiscount(value)
  }

  const handleSuperDiscountChange = (value) => {
    setSuperDiscount(value)
  }

  const handleParticipateAllChange = (value) => {
    setParticipateAll(value)
  }

  const handleSelectAllProgramsChange = (value) => {
    if (value === false && sellForEquity === true && originalData?.sellForEquity === true) {
      setPendingLeaveAction('selectAll')
      setIsLeaveModalOpen(true)
      return
    }

    setSelectAllPrograms(value)

    if (value === true) {
      setSellForEquity(true)
      setSellOnOtherPlatforms(true)
    } else {
      setSellForEquity(false)
      setSellOnOtherPlatforms(false)
    }
  }

  const handleSellForEquityChange = (value) => {
    if (value === false && sellForEquity === true && originalData?.sellForEquity === true) {
      setPendingLeaveAction('sellForEquity')
      setIsLeaveModalOpen(true)
      return
    }

    setSellForEquity(value)

    if (value === true && sellOnOtherPlatforms === true) {
      setSelectAllPrograms(true)
    }
  }

  const handleSellOnOtherPlatformsChange = (value) => {
    setSellOnOtherPlatforms(value)

    if (value === true && sellForEquity === true) {
      setSelectAllPrograms(true)
    } else if (value === false && selectAllPrograms === true) {
      setSelectAllPrograms(false)
    }
  }

  const handleLeaveModalClose = () => {
    setIsLeaveModalOpen(false)
    setPendingLeaveAction(null)
  }

  const handleLeaveModalConfirm = async () => {
    try {
      setIsSaving(true)

      let updatedSellForEquity = sellForEquity
      let updatedSellOnOtherPlatforms = sellOnOtherPlatforms
      let updatedSelectAllPrograms = selectAllPrograms

      if (pendingLeaveAction === 'sellForEquity') {
        updatedSellForEquity = false
        updatedSelectAllPrograms = false
      } else if (pendingLeaveAction === 'selectAll') {
        updatedSellForEquity = false
        updatedSellOnOtherPlatforms = false
        updatedSelectAllPrograms = false
      }

      setSellForEquity(updatedSellForEquity)
      setSellOnOtherPlatforms(updatedSellOnOtherPlatforms)
      setSelectAllPrograms(updatedSelectAllPrograms)

      const discountData = {
        regularDiscount,
        superDiscount,
        participateAll,
        sellForEquity: updatedSellForEquity,
        sellOnOtherPlatforms: updatedSellOnOtherPlatforms,
        selectAllPrograms: updatedSelectAllPrograms,
      }

      await mySalesService.saveDiscounts(discountData)

      setOriginalData({
        ...originalData,
        discount: parseInt(regularDiscount, 10),
        superDiscount: parseInt(superDiscount, 10),
        saleOff: participateAll,
        sellForEquity: updatedSellForEquity,
        sellOnOtherPlatforms: updatedSellOnOtherPlatforms,
        selectAllPrograms: updatedSelectAllPrograms,
      })

      setShowForceOptInWarning(false)

      if (updatedSellForEquity !== originalData?.sellForEquity) {
        App.events.publish('subscription:participation:changed', updatedSellForEquity)
      }
      App.events.publish(
        'app:notification:flash:success',
        'You have successfully left the Maximize your earnings program'
      )
    } catch (error) {
      App.events.publish(
        'app:notification:flash:error',
        'Something went wrong. Please try again.'
      )
    } finally {
      setIsSaving(false)
      setIsLeaveModalOpen(false)
      setPendingLeaveAction(null)
    }
  }

  const handleSaveChanges = async () => {
    try {
      setIsSaving(true)

      const discountData = {
        regularDiscount,
        superDiscount,
        participateAll,
        sellForEquity,
        sellOnOtherPlatforms,
        selectAllPrograms,
      }

      await mySalesService.saveDiscounts(discountData)

      if (sellForEquity !== originalData?.sellForEquity) {
        App.events.publish('subscription:participation:changed', sellForEquity)
      }

      setOriginalData({
        ...originalData,
        discount: parseInt(regularDiscount, 10),
        superDiscount: parseInt(superDiscount, 10),
        saleOff: participateAll,
        sellForEquity,
        sellOnOtherPlatforms,
        selectAllPrograms,
      })

      setShowForceOptInWarning(false)

      App.events.publish(
        'app:notification:flash:success',
        'You successfully saved your discount settings.'
      )

      setHasChanges(false)
    } catch (error) {
      App.events.publish(
        'app:notification:flash:error',
        'Failed to save discount settings. Please try again.'
      )
    } finally {
      setIsSaving(false)
    }
  }

  const handleJoinProgram = async () => {
    try {
      setIsSaving(true)

      const updatedSellForEquity = true
      const updatedSelectAllPrograms = sellOnOtherPlatforms
      setSellForEquity(updatedSellForEquity)
      setSelectAllPrograms(updatedSelectAllPrograms)

      const discountData = {
        regularDiscount,
        superDiscount,
        participateAll,
        sellForEquity: updatedSellForEquity,
        sellOnOtherPlatforms,
        selectAllPrograms: updatedSelectAllPrograms,
      }

      await mySalesService.saveDiscounts(discountData)

      const newOriginalData = {
        ...originalData,
        discount: parseInt(regularDiscount, 10),
        superDiscount: parseInt(superDiscount, 10),
        saleOff: participateAll,
        sellForEquity: updatedSellForEquity,
        sellOnOtherPlatforms,
        selectAllPrograms: updatedSelectAllPrograms,
      }
      setOriginalData(newOriginalData)

      setShowForceOptInWarning(false)

      if (!originalData?.sellForEquity) {
        App.events.publish('subscription:participation:changed', true)
      }
      App.events.publish(
        'app:notification:flash:success',
        'You have successfully joined the Maximize your earnings program'
      )
    } catch (error) {
      App.events.publish(
        'app:notification:flash:error',
        'Something went wrong. Please try again.'
      )
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancelChanges = () => {
    if (originalData) {
      setRegularDiscount(
        originalData.discount?.toString() || DEFAULT_REGULAR_DISCOUNT
      )
      setSuperDiscount(
        originalData.superDiscount?.toString() || DEFAULT_SUPER_DISCOUNT
      )
      setParticipateAll(originalData.saleOff ?? false)
      setSellForEquity(originalData.sellForEquity ?? false)
      setSellOnOtherPlatforms(originalData.sellOnOtherPlatforms ?? false)
      setSelectAllPrograms(originalData.selectAllPrograms ?? false)
      setHasChanges(false)
    }
  }

  return {
    regularDiscount,
    superDiscount,
    participateAll,
    selectAllPrograms,
    sellForEquity,
    sellOnOtherPlatforms,
    isLoading,
    isSaving,
    handleRegularDiscountChange,
    handleSuperDiscountChange,
    handleParticipateAllChange,
    handleSelectAllProgramsChange,
    handleSellForEquityChange,
    handleSellOnOtherPlatformsChange,
    handleJoinProgram,
    handleSaveChanges,
    handleCancelChanges,
    hasChanges,
    isSellForEquitySaved: originalData?.sellForEquity ?? false,
    isLeaveModalOpen,
    handleLeaveModalClose,
    handleLeaveModalConfirm,
    showForceOptInWarning,
  }
}
